import { Box, Stack, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ValuationHubCoreModelDtoValuationReportConfigurationDto } from "@/api/generatedApi";
import { InfoIcon } from "@/assets";
import { CloudUploadIcon } from "@/assets/icons/CloudUploadIcon";
import { ProfileUploadIcon } from "@/assets/icons/ProfileUploadIcon";
import { ColorPicker } from "@/components/colorPicker/ColorPicker";
import { EnhancedTooltip } from "@/components/enhancedTooltip";
import { ImageUpload } from "@/components/imageUpload";
import { useModuleContext } from "@/hooks/useModuleContext";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  infoIcon: {
    marginLeft: 1,
    fontSize: theme.analysisLayout.titleFontSize,
    color: "primary.main",
  },
  columnsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.analysisLayout.sectionGap,
    width: "100%",
  },
  column: {
    width: "100%",
    gap: "8px",
  },
});

interface LogoAndColorsSectionProps {
  disabled?: boolean;
  onImagePendingChange?: (value: boolean) => void;
}

export const LogoAndColorsSection = ({
  disabled = false,
  onImagePendingChange,
}: LogoAndColorsSectionProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("ValuationReportGenerationPanel");
  const { isValuationReport } = useModuleContext();
  const { watch, setValue } = useFormContext<ValuationHubCoreModelDtoValuationReportConfigurationDto>();

  const maxFileSize = 5;
  const defaultPrimaryColor = isValuationReport ? "#18EFC4" : "#1F497D";
  const primaryColor = watch("primary_color") || defaultPrimaryColor;
  const companyLogo = watch("company_logo");
  const profilePicture = watch("profile_picture");

  const onImageChange = (payload: string | null, field: "company_logo" | "profile_picture"): void => {
    if (!payload) {
      setValue(field, null);
      return;
    }
    setValue(field, payload);
  };

  const onColorChange = (color: string): void => {
    setValue("primary_color", color);
  };

  return (
    <Box sx={styles.columnsGrid}>
      <Stack alignItems={"start"} direction="column" sx={styles.column} data-cy="report-configuration-company-logo">
        <Stack alignItems={"start"} direction="row">
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.companyLogo")}</Typography>
          <EnhancedTooltip content={t("reportPanel.configuration.companyLogoTooltip")} placement="top">
            <Box>
              <InfoIcon sx={styles.infoIcon} />
            </Box>
          </EnhancedTooltip>
        </Stack>
        <ImageUpload
          uploadIcon={<CloudUploadIcon sx={{ fontSize: "40px" }} />}
          uploadTypesText={t("reportPanel.configuration.logoTypes", { maxFileSize })}
          titleMain={t("reportPanel.configuration.companyLogoUpload")}
          titleSub={t("reportPanel.configuration.dragAndDrop")}
          removeImageText={t("reportPanel.configuration.removeImage")}
          onPendingChange={onImagePendingChange}
          onImageUpload={(file) => onImageChange(file, "company_logo")}
          value={companyLogo}
          disabled={disabled}
          maxFileSize={maxFileSize}
        />
      </Stack>
      <Stack alignItems={"start"} direction="column" sx={styles.column} data-cy="report-configuration-profile-logo">
        <Stack alignItems={"start"} direction="row">
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.profileLogo")}</Typography>
          <EnhancedTooltip content={t("reportPanel.configuration.profileLogoTooltip")} placement="top">
            <Box>
              <InfoIcon sx={styles.infoIcon} />
            </Box>
          </EnhancedTooltip>
        </Stack>
        <ImageUpload
          uploadIcon={<ProfileUploadIcon sx={{ fontSize: "40px" }} />}
          uploadTypesText={t("reportPanel.configuration.logoTypes", { maxFileSize })}
          titleMain={t("reportPanel.configuration.profileLogoUpload")}
          titleSub={t("reportPanel.configuration.dragAndDrop")}
          removeImageText={t("reportPanel.configuration.removeImage")}
          onPendingChange={onImagePendingChange}
          onImageUpload={(file) => onImageChange(file, "profile_picture")}
          value={profilePicture}
          disabled={disabled}
          maxFileSize={maxFileSize}
        />
      </Stack>
      <Stack alignItems={"start"} direction="column" sx={styles.column}>
        <Stack alignItems={"start"} direction="row">
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.colors")}</Typography>
          <EnhancedTooltip content={t("reportPanel.configuration.colorsTooltip")} placement="top">
            <Box>
              <InfoIcon sx={styles.infoIcon} />
            </Box>
          </EnhancedTooltip>
        </Stack>
        <Typography>{t("reportPanel.configuration.primaryColor")}</Typography>
        <ColorPicker color={primaryColor} onColorChange={(color) => onColorChange(color)} />
      </Stack>
    </Box>
  );
};
